





// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';
import Endpoints from '@/constants/endpoints';

// Third party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Constants
import Assets from '@/constants/assets.ts';
import RouteNames from '@/constants/route-names';

// Components
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import ConvocationRow, {
    ParticipantListType,
} from '@/manager/convocation/views/convocationDetail/convocationRow/convocationRow.view.vue';
import DropUp, { DropUpColorHover } from '@/shared/components/DropUp.component.vue';
import GroupFilter from '@/shared/components/GroupFilter.component.vue';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import ItemThumb from '@/shared/components/item-thumb.component.vue';
import Spinner from '@/shared/components/Spinner.component.vue';
import TableColumnOrder from '@/shared/components/TableColumnOrder.component.vue';
import EmptyState from '@/shared/components/empty-state.component.vue';
import SpotlightText, {
  SpotlightTextColor,
} from '@/shared/components/SpotlightText.component.vue';

// Models - Interfaces
import { CounterInterface } from '@/shared/components/counter/counter.interface.ts';
import { TimetablesInterface } from '@/shared/interfaces/timetables.interface';
import { Participant } from '@/manager/convocation/participant.model';
import { SecondLevelOptionModel } from '@/shared/models/secondLevelOption.model';
import { Convocation } from '@/manager/convocation/convocation.model';
import { CounterColor } from '@/shared/components/counter/counter.enum.ts';

// Services
import { ConvocationService } from '@/manager/convocation/convocation.service';

// Mixins
import WithSpinner from '@/shared/mixins/withSpinner.mixin.ts';
import WithPagination from '@/shared/mixins/withPagination.mixin.ts';
import ColumnOrderMixin from '@/shared/mixins/columnOrder.mixin.ts';
import SearchMixin from '@/shared/mixins/search.mixin.ts';
import NotificationMessage from '@/shared/mixins/notificationMessage.mixin.ts';


export enum ListActions {
    Inscrition = 'inscription',
    DownloadCsv = 'download-csv',
    EmailToMultiple = 'email-to-multiple',
    ToPurchases = 'to-purchases',
}

export interface Filters {
    inscribed: boolean;
    confirmed: boolean;
    not_confirmed: boolean;
    checked: boolean;
    sales_amount: boolean;
    summoned: boolean;
    out: boolean;
    abandonment: boolean;
}

interface MixinInterface extends WithPagination, SearchMixin, ColumnOrderMixin, NotificationMessage { }

@Component({
    components: {
        Btn,
        ConvocationRow,
        DropUp,
        EmptyState,
        GroupFilter,
        Icon,
        ItemThumb,
        Pagination,
        Spinner,
        TableColumnOrder,
        SpotlightText
    },
    computed: {
        ...mapGetters({
            getConvocation: 'getConvocation',
            getParticipantList: 'getParticipantList',
            participantMassiveEmailList: 'getParticipantMassiveEmailList',
        }),
    },
})
export default class ConferenceParticipantList extends Mixins<MixinInterface>(
    WithPagination,
    SearchMixin,
    ColumnOrderMixin,
    NotificationMessage
) {
    @Prop() private convocationId!: string;
    @Prop() private isLive!: boolean;
    @Prop() private parsedNextConvocationListOptions!: any[];
    @Prop() private initialNextConvocationListIndex!: number;
    @Prop() private paymentStatusOptions!: any[];
    @Prop() private columnsVisibility!: any;
    @Prop() private searchValue!: string;
    @Prop() private searchNifValue!: string;
    @Prop() private headerClass!: string;
    @Prop() private isBeforeConfirmationPeriod!: boolean;

    private assets = Assets;
    private dropUpColorHover = DropUpColorHover;
    private iconEnum = IconEnum;
    private btnColor = BtnColor;
    private btnWidth = BtnWidth;
    private getConvocation!: any;
    private participantMassiveEmailList!: any;
    private loadingGroupFilters = true;
    private participantListType = ParticipantListType;
    private getParticipantList!: Participant[];
    private isOpenNavbar!: any;
    private lastNoteTyped: string = '';
    private interval!: any;
    private cleanSearch = false;
    private participantListLoaded = false;

    private filters: Filters = {
        inscribed: true,
        confirmed: false,
        not_confirmed: false,
        checked: false,
        sales_amount: false,
        abandonment: false,
        out: false,
        summoned: false
    };

    @Watch('searchValue')
    public watchSearchValue(newVal: any, oldVal: any) {
        this.searchString = this.searchValue;
    }

    @Watch('searchNifValue')
    public watchSearchNifValue(newVal: any, oldVal: any) {
        this.searchNifString = this.searchNifValue;
    }

    get convocation() {
        return this.getConvocation(this.convocationId);
    }

    get showPagination() {
        return this.participantListLoaded && !this.spinner.show && this.getParticipantList.length > 0;
    }

    get showTable() {
        const result = (
            this.participantListLoaded &&
            !this.spinner.show &&
            !this.showEmptyState
        );
        this.$emit('show-table', result);
        return result;
    }

    get dropUpOption() {
        return [
            { value: ListActions.Inscrition, name: 'Formulario inscripción' },
            { value: ListActions.DownloadCsv, name: 'Exportar lista' },
            { value: ListActions.EmailToMultiple, name: 'Email masivo' },
            { value: ListActions.ToPurchases, name: 'Ir a compras' },
        ];
    }

    get counterFilters(): CounterInterface[] {
        const counters = [
            {
                value: this.convocation.totalParticipantEnrolled,
                title: 'Inscritos',
                active: this.filters.inscribed,
                color: CounterColor.Secondary,
                filter: 'summoned',
                visible: true,
            }, {
                value: this.convocation.totalParticipantConfirmed,
                title: 'Confirmados',
                active: this.filters.confirmed,
                color: CounterColor.Primary,
                filter: 'confirmed',
                visible: true,
            }, {
                value: this.convocation.totalParticipantNotConfirmed,
                title: 'No confirmados',
                active: this.filters.not_confirmed,
                color: CounterColor.GreyDark,
                filter: 'not_confirmed',
                visible: true,
            }, {
                value: this.convocation.totalParticipantChecked,
                title: 'Registrados',
                active: this.filters.checked,
                color: CounterColor.Primary,
                filter: 'checked',
                visible: this.isLive,
            }, {
                value: (this.convocation) ? this.convocation.totalParticipantAbandonment : 0,
                title: 'Abandonos',
                active: this.filters.abandonment,
                color: CounterColor.BlueDark,
                filter: 'abandonment',
                visible: this.isLive,
            }, {
                value: (this.convocation) ? this.convocation.totalParticipantOut : 0,
                title: 'Bajas',
                active: this.filters.out,
                color: CounterColor.BlueDark,
                filter: 'out',
                visible: true,
            }, {
                value: (this.convocation) ? this.convocation.totalParticipantSummoned : 0,
                title: 'Total',
                active: this.filters.summoned,
                color: CounterColor.Secondary,
                filter: 'summoned',
                visible: true,
            }, {
                value: this.convocation.totalParticipantSalesAmount,
                title: 'Compras',
                active: this.filters.sales_amount,
                color: CounterColor.Secondary,
                filter: 'sales_amount',
                visible: this.isLive,
            },
        ];

        return counters;
    }

    get showEmptyState() {
        return (
            this.participantListLoaded &&
            this.getParticipantList.length === 0 &&
            !this.spinner.show
        );
    }

    public fetchList() {
        this.requestShowSpinner();
        this.$emit('showing-spinner');
        this.fetchParticipantList((res: any) => {
            this.onFetchSuccess(res);
            this.$emit('list-loaded', res);
            this.participantListLoaded = true;
            this.changeSpinnerVisibility(false);
        });
    }

    public searchCallback() {
        this.fetchFirstPageList();
    }

    private created() {
        this.changeSpinnerVisibility(true);
        this.searchString = this.searchValue;
        this.searchNifString = this.searchNifValue;
        this.fetchFirstPageList();
    }

    private refetchList() {
        this.$emit('refetch-list');
    }

    private formatConvocationDates(convocation: Convocation): string {
        return ConvocationService.formatDates(
            convocation.timetables as TimetablesInterface[],
        );
    }

    // tslint:disable-next-line:member-ordering
    public columnOrderFetchList() {
        this.requestShowSpinner();
        this.fetchParticipantList(
            (res: any) => {
                this.columnOrderOnFetchSuccess();
                this.changeSpinnerVisibility(false);
            },
        );
    }

    private handleComeBack() {
        this.filters = {
            inscribed: true,
            confirmed: false,
            not_confirmed: false,
            checked: false,
            sales_amount: false,
            summoned: false,
            abandonment: false,
            out: false,
        };
        this.cleanSearch = !this.cleanSearch;
        if (this.searchString === '') {
            this.fetchFirstPageList();
        } else {
            this.searchString = '';
        }
    }

    private fetchParticipantChecked() {
        this.$store.dispatch(
            'fetchParticipantChecked',
            { convocationId: this.convocationId },
        );
    }

    private fetchParticipantList(callback: any = () => { const doNothing = 1; }) {
        this.$store.dispatch(
            'fetchParticipantList',
            {
                id: this.convocationId,
                offset: this.offset,
                limit: this.limit,
                filter: this.filters,
                ordering: this.composeOrdering(),
                search: this.searchString,
                searchNif: this.searchNifString,
            },
        ).then((res: any) => {
            this.changeSpinnerVisibility(false);
            if (callback) {
                callback(res);
            }
        })
            .catch((err) => {
                this.changeSpinnerVisibility(false);
            });
    }

    private fetchParticipantMassiveEmailList(callback: any = () => { const doNothing = 1; }) {
        this.$store.dispatch('fetchParticipantMassiveEmail', {
            convocationId: this.convocationId,
            params: {},
        }).then(
            (res) => {
                callback();
            },
        );
    }

    private changeFilter(counter: CounterInterface) {
        this.filters = {
            confirmed: false,
            inscribed: false,
            not_confirmed: false,
            checked: false,
            sales_amount: false,
            summoned: false,
            abandonment: false,
            out: false,
        };
        switch (counter.filter) {
            case 'summoned':
                this.filters.inscribed = true;
                break;
            case 'not_confirmed':
                this.filters.not_confirmed = true;
                break;
            case 'confirmed':
                this.filters.confirmed = true;
                break;
            case 'checked':
                this.filters.checked = true;
                break;
            case 'sales_amount':
                this.filters.sales_amount = true;
                break;
        }
        this.requestShowSpinner();
        this.fetchFirstPageList();
    }

    private dropUpOptionSelected(option: any) {
        switch (option.value) {
            case ListActions.Inscrition:
                // const url = `/#/public/convocation/${this.convocation.id}/participant/inscription`;
                // window.open(url, '_blank');
                this.generateInscriptionForm();
                break;
            case ListActions.DownloadCsv:
                ConvocationService.downloadCsv(this.convocationId, this.filters);
                break;
            case ListActions.EmailToMultiple:
                this.fetchParticipantMassiveEmailList(
                    () => { ConvocationService.mailToFilteredUsers(this.participantMassiveEmailList); },
                );
                break;
            case ListActions.ToPurchases:
                this.$router.push({
                    name: RouteNames.PurchaseConvocationDetail, params: {
                        id: this.convocationId,
                    }
                });
                break;
        }
    }

    private showMessage(message: string) {
        this.$emit('show-message', message);
    }

    generateInscriptionForm() {
        // Construir la URL con el ID de convocatoria
        const urljs = `${window.location.href.split("#")[0]}api/api/static/js/generate-convocation-form.js?id=${this.convocation.id}`;

        // Crear el contenido del script con la URL ya generada y escapar comillas dobles
        const scriptContent = `<div id="convocation-inscription"></div>
<script src='${urljs}'><\/script>`;

        // Copiar el contenido del script al portapapeles
        const navigatorAny: any = navigator
        navigatorAny.clipboard.writeText(scriptContent)
            .then(() => {
                let message = "Script copiado al portapapeles"
                this.showNotification(
                    message,
                    SpotlightTextColor.Primary,
                );
                console.log(message);
            })
            .catch((error: any) => {
                this.showNotification(
                    "Error al copiar el script",
                    SpotlightTextColor.Error,
                );
                console.error("Error al copiar el script:", error)
            });
    }

}
